// Bash scripting lessons - base set (26 lessons)
const baseBashLessons = [
  {
    title: "Introduction to Bash",
    description: "Let's start with a basic command to print text to the terminal. The 'echo' command is used to display a line of text.",
    task: "Use the echo command to print 'Hello, World!' to the terminal.",
    hint: "The echo command is followed by the text you want to display.",
    example: "echo Hello",
    answer: "echo Hello, World!",
    tips: [
      "Don't forget to include the comma and exclamation mark",
      "You can use quotes for text with spaces, but they're not required"
    ],
    commandRef: {
      command: "echo",
      description: "Display a line of text or variable value to standard output",
      syntax: "echo [OPTIONS] [STRING...]",
      options: [
        { flag: "-n", description: "Do not output a trailing newline" },
        { flag: "-e", description: "Enable interpretation of backslash escapes" }
      ],
      examples: [
        { code: "echo Hello World", description: "Print text to terminal" },
        { code: "echo -n \"No newline\"", description: "Print without newline at end" },
        { code: "echo $HOME", description: "Print the value of a variable" }
      ]
    }
  },
  {
    title: "Basic File Navigation",
    description: "The 'ls' command is used to list files and directories in the current directory.",
    task: "Use the ls command to list all files, including hidden ones (files that start with a dot).",
    hint: "You need to add an option to the ls command to show hidden files.",
    example: "ls -l (shows detailed listing)",
    answer: ["ls -a", "ls -la", "ls -al", "ls --all"],
    tips: [
      "Options in bash commands are usually preceded by a hyphen (-)",
      "Multiple single-letter options can be combined after a single hyphen"
    ],
    commandRef: {
      command: "ls",
      description: "List directory contents, showing files and folders",
      syntax: "ls [OPTIONS] [FILE...]",
      options: [
        { flag: "-a", description: "Show all files, including hidden ones (starting with .)" },
        { flag: "-l", description: "Use long listing format with details" },
        { flag: "-h", description: "Human-readable sizes (KB, MB, GB)" },
        { flag: "-R", description: "List subdirectories recursively" }
      ],
      examples: [
        { code: "ls", description: "List files in current directory" },
        { code: "ls -la", description: "Detailed list including hidden files" },
        { code: "ls -lh /var/log", description: "List /var/log with human-readable sizes" }
      ]
    }
  },
  {
    title: "File Manipulation",
    description: "The 'mkdir' command creates new directories.",
    task: "Create a new directory called 'projects'.",
    hint: "The directory name follows the mkdir command.",
    example: "mkdir documents",
    answer: "mkdir projects",
    tips: [
      "Make sure to spell the directory name correctly",
      "For directories with spaces, use quotes: mkdir 'my projects'"
    ],
    commandRef: {
      command: "mkdir",
      description: "Create new directories (folders)",
      syntax: "mkdir [OPTIONS] DIRECTORY...",
      options: [
        { flag: "-p", description: "Create parent directories as needed, no error if exists" },
        { flag: "-v", description: "Print a message for each created directory" }
      ],
      examples: [
        { code: "mkdir mydir", description: "Create a single directory" },
        { code: "mkdir -p a/b/c", description: "Create nested directories" },
        { code: "mkdir dir1 dir2 dir3", description: "Create multiple directories at once" }
      ]
    }
  },
  {
    title: "Variables in Bash",
    description: "In Bash, you can create variables to store data. Variable names are case-sensitive.",
    task: "Create a variable named 'name' with your name and echo it using the $ symbol.",
    hint: "First assign a value to the variable, then use echo with $ to display it.",
    example: "message='hello'\necho $message",
    answer: ["name=John\necho $name", "name=\"John\"\necho $name", "name='John'\necho $name"],
    tips: [
      "No spaces are allowed around the equals sign in variable assignment",
      "You can use double quotes, single quotes, or no quotes for simple strings"
    ]
  },
  {
    title: "Conditional Statements",
    description: "Bash uses 'if' statements for conditional execution. The basic syntax uses square brackets for condition testing.",
    task: "Write a simple if statement that checks if a variable 'x' equals 10 and echoes 'Equal' if true.",
    hint: "Remember the spaces inside the square brackets are required.",
    example: "if [ $a = 5 ]; then\n  echo 'a is 5'\nfi",
    answer: ["if [ $x = 10 ]; then\n  echo Equal\nfi", "if [ $x -eq 10 ]; then\n  echo Equal\nfi"],
    tips: [
      "The 'if' statement must end with 'fi'",
      "The then keyword must be on the same line as the condition, separated by a semicolon, or on the next line"
    ]
  },
  {
    title: "Changing Directories",
    description: "The 'cd' command is used to change your current directory location.",
    task: "Write the command to change to the 'Documents' directory.",
    hint: "The cd command is followed by the path you want to navigate to.",
    example: "cd Downloads",
    answer: "cd Documents",
    tips: [
      "Paths can be absolute (starting with /) or relative to your current location",
      "Use 'cd ..' to move up one directory level"
    ]
  },
  {
    title: "Current Directory",
    description: "The 'pwd' command shows your current directory location.",
    task: "Enter the command to display your current working directory.",
    hint: "This command has no arguments or options needed.",
    example: "p__ (fill in the missing letters)",
    answer: "pwd",
    tips: [
      "pwd stands for 'print working directory'",
      "This is useful when you need to confirm your location in the file system"
    ]
  },
  {
    title: "Creating Files",
    description: "The 'touch' command creates empty files or updates the timestamp of existing files.",
    task: "Create an empty file named 'notes.txt'.",
    hint: "The filename follows the touch command.",
    example: "touch example.txt",
    answer: "touch notes.txt",
    tips: [
      "This won't overwrite existing files, only update their timestamp",
      "You can create multiple files at once: touch file1.txt file2.txt"
    ]
  },
  {
    title: "Removing Files",
    description: "The 'rm' command deletes files from your system.",
    task: "Write the command to remove a file named 'temp.txt'.",
    hint: "The filename follows the rm command.",
    example: "rm oldfile.txt",
    answer: "rm temp.txt",
    tips: [
      "Be careful with this command as it permanently deletes files",
      "Use the -i option for a confirmation prompt: rm -i filename"
    ],
    commandRef: {
      command: "rm",
      description: "Remove (delete) files or directories permanently",
      syntax: "rm [OPTIONS] FILE...",
      options: [
        { flag: "-i", description: "Prompt before every removal" },
        { flag: "-r", description: "Remove directories and their contents recursively" },
        { flag: "-f", description: "Force removal without prompting" },
        { flag: "-v", description: "Verbose mode - explain what is being done" }
      ],
      examples: [
        { code: "rm file.txt", description: "Delete a single file" },
        { code: "rm -i *.log", description: "Delete all .log files with confirmation" },
        { code: "rm -rf old_folder/", description: "Force delete folder and contents" }
      ]
    }
  },
  {
    title: "Removing Directories",
    description: "The 'rmdir' command removes empty directories, while 'rm -r' removes directories and their contents.",
    task: "Write the command to recursively remove a directory called 'old_backups' and all its contents.",
    hint: "You need the rm command with a specific option for recursive deletion.",
    example: "rm -r old_folder",
    answer: ["rm -r old_backups", "rm -rf old_backups"],
    tips: [
      "The -r option means recursive (delete contents too)",
      "Adding -f forces deletion without confirmation, use with caution"
    ],
    commandRef: {
      command: "rm",
      description: "Remove (delete) files or directories permanently",
      syntax: "rm [OPTIONS] FILE...",
      options: [
        { flag: "-r", description: "Remove directories and their contents recursively" },
        { flag: "-f", description: "Force removal without prompting" },
        { flag: "-i", description: "Prompt before every removal" }
      ],
      examples: [
        { code: "rm -r mydir/", description: "Delete directory and all its contents" },
        { code: "rm -rf temp/", description: "Force delete without confirmation" },
        { code: "rmdir emptydir", description: "Delete only if directory is empty" }
      ]
    }
  },
  {
    title: "Copying Files",
    description: "The 'cp' command copies files or directories.",
    task: "Write the command to copy 'source.txt' to a new file named 'backup.txt'.",
    hint: "The cp command takes a source and destination path.",
    example: "cp original.txt copy.txt",
    answer: "cp source.txt backup.txt",
    tips: [
      "Use -r option to copy directories and their contents",
      "The destination can be a different directory: cp file.txt /path/to/directory/"
    ],
    commandRef: {
      command: "cp",
      description: "Copy files and directories",
      syntax: "cp [OPTIONS] SOURCE DEST",
      options: [
        { flag: "-r", description: "Copy directories recursively" },
        { flag: "-i", description: "Prompt before overwriting" },
        { flag: "-v", description: "Verbose - show files being copied" },
        { flag: "-p", description: "Preserve file attributes (timestamp, permissions)" }
      ],
      examples: [
        { code: "cp file.txt backup.txt", description: "Copy file to new name" },
        { code: "cp -r src/ dest/", description: "Copy entire directory" },
        { code: "cp *.txt /backup/", description: "Copy all .txt files to backup folder" }
      ]
    }
  },
  {
    title: "Moving Files",
    description: "The 'mv' command moves files or directories from one location to another. It's also used for renaming.",
    task: "Write the command to rename 'old.txt' to 'new.txt'.",
    hint: "The mv command takes a source and destination path.",
    example: "mv source.txt destination.txt",
    answer: "mv old.txt new.txt",
    tips: [
      "This command also works for moving files to different directories",
      "Example: mv file.txt /path/to/directory/"
    ]
  },
  {
    title: "Viewing File Contents",
    description: "The 'cat' command displays the contents of files.",
    task: "Write the command to display the content of 'readme.txt'.",
    hint: "The filename follows the cat command.",
    example: "cat info.txt",
    answer: "cat readme.txt",
    tips: [
      "For large files, consider using 'less' instead of 'cat'",
      "You can view multiple files at once: cat file1.txt file2.txt"
    ],
    commandRef: {
      command: "cat",
      description: "Concatenate and display file contents to standard output",
      syntax: "cat [OPTIONS] [FILE...]",
      options: [
        { flag: "-n", description: "Number all output lines" },
        { flag: "-b", description: "Number non-blank output lines" },
        { flag: "-s", description: "Squeeze multiple blank lines into one" }
      ],
      examples: [
        { code: "cat file.txt", description: "Display file contents" },
        { code: "cat file1.txt file2.txt", description: "Display multiple files" },
        { code: "cat -n file.txt", description: "Display with line numbers" }
      ]
    }
  },
  {
    title: "Viewing Large Files",
    description: "The 'less' command allows scrolling through large files.",
    task: "Write the command to view a large file called 'log.txt' with the ability to scroll.",
    hint: "The filename follows the less command.",
    example: "less large_file.txt",
    answer: "less log.txt",
    tips: [
      "Use arrow keys or Page Up/Down to navigate",
      "Press 'q' to quit, '/' to search"
    ],
    commandRef: {
      command: "less",
      description: "View file contents with scrolling capability (better than cat for large files)",
      syntax: "less [OPTIONS] FILE",
      options: [
        { flag: "-N", description: "Show line numbers" },
        { flag: "-S", description: "Chop long lines instead of wrapping" },
        { flag: "+F", description: "Follow mode (like tail -f)" }
      ],
      examples: [
        { code: "less file.txt", description: "View file with scrolling" },
        { code: "less -N file.txt", description: "View with line numbers" },
        { code: "less +G file.txt", description: "Start at end of file" }
      ]
    }
  },
  {
    title: "Head and Tail",
    description: "The 'head' and 'tail' commands show the beginning or end of files.",
    task: "Write the command to show the last 10 lines of 'server.log'.",
    hint: "Use the tail command followed by the filename.",
    example: "tail log.txt",
    answer: "tail server.log",
    tips: [
      "Add -n option to specify a different number of lines: tail -n 20 file.txt",
      "Use tail -f to follow file updates in real-time"
    ],
    commandRef: {
      command: "tail",
      description: "Output the last part of files (default: last 10 lines)",
      syntax: "tail [OPTIONS] [FILE...]",
      options: [
        { flag: "-n NUM", description: "Output the last NUM lines" },
        { flag: "-f", description: "Follow - output appended data as file grows" },
        { flag: "-c NUM", description: "Output the last NUM bytes" }
      ],
      examples: [
        { code: "tail file.txt", description: "Show last 10 lines" },
        { code: "tail -n 20 file.txt", description: "Show last 20 lines" },
        { code: "tail -f /var/log/syslog", description: "Follow log file in real-time" }
      ]
    }
  },
  {
    title: "Finding Files",
    description: "The 'find' command searches for files in a directory hierarchy.",
    task: "Write a command to find all .txt files in the current directory and its subdirectories.",
    hint: "Use find with the -name option to search by filename pattern.",
    example: "find . -name '*.jpg'",
    answer: ["find . -name '*.txt'", "find . -name \"*.txt\""],
    tips: [
      "The first parameter (.) specifies where to start searching",
      "Use quotes around the pattern to prevent shell expansion"
    ],
    commandRef: {
      command: "find",
      description: "Search for files in a directory hierarchy based on various criteria",
      syntax: "find [PATH] [OPTIONS] [EXPRESSION]",
      options: [
        { flag: "-name PATTERN", description: "Search by filename (case-sensitive)" },
        { flag: "-iname PATTERN", description: "Search by filename (case-insensitive)" },
        { flag: "-type f", description: "Find only files" },
        { flag: "-type d", description: "Find only directories" },
        { flag: "-mtime -N", description: "Modified in last N days" }
      ],
      examples: [
        { code: "find . -name '*.txt'", description: "Find all .txt files" },
        { code: "find /home -type d", description: "Find all directories in /home" },
        { code: "find . -mtime -7", description: "Files modified in last 7 days" }
      ]
    }
  },
  {
    title: "Counting Words and Lines",
    description: "The 'wc' command counts lines, words, and characters in files.",
    task: "Write the command to count the number of lines in 'data.csv'.",
    hint: "Use wc with an option to only show the line count.",
    example: "wc -l file.txt",
    answer: "wc -l data.csv",
    tips: [
      "The -l option counts lines",
      "Use -w for word count and -c for character count"
    ]
  },
  {
    title: "File Permissions",
    description: "The 'chmod' command changes file permissions. Permissions control who can read, write, or execute a file.",
    task: "Write the command to make 'script.sh' executable for the owner.",
    hint: "Use chmod with a symbolic mode to add execution permission.",
    example: "chmod +x program",
    answer: ["chmod +x script.sh", "chmod u+x script.sh"],
    tips: [
      "u+x means add execute permission for the user (owner)",
      "You can also use numeric modes like 755 or 644"
    ],
    commandRef: {
      command: "chmod",
      description: "Change file access permissions (read, write, execute)",
      syntax: "chmod [OPTIONS] MODE FILE...",
      options: [
        { flag: "+x", description: "Add execute permission for all" },
        { flag: "u+x", description: "Add execute for user (owner) only" },
        { flag: "755", description: "rwxr-xr-x (owner: all, others: read+execute)" },
        { flag: "644", description: "rw-r--r-- (owner: read+write, others: read)" },
        { flag: "-R", description: "Change permissions recursively" }
      ],
      examples: [
        { code: "chmod +x script.sh", description: "Make file executable" },
        { code: "chmod 755 script.sh", description: "Set rwxr-xr-x permissions" },
        { code: "chmod -R 644 dir/", description: "Set rw-r--r-- recursively" }
      ]
    }
  },
  {
    title: "File Ownership",
    description: "The 'chown' command changes file ownership.",
    task: "Write the command to change the owner of 'data.txt' to user 'admin'.",
    hint: "The syntax is chown [user] [file].",
    example: "chown user1 file.txt",
    answer: "chown admin data.txt",
    tips: [
      "You need appropriate permissions to change ownership",
      "You can also change group with chown user:group file"
    ]
  },
  {
    title: "Basic For Loop",
    description: "Bash allows for loops to iterate over a list of items or a range of values.",
    task: "Write a simple for loop that echoes the numbers 1 to 5.",
    hint: "Use the for...in syntax with a sequence of numbers.",
    example: "for i in a b c; do\n  echo $i\ndone",
    answer: ["for i in 1 2 3 4 5; do\n  echo $i\ndone", "for i in {1..5}; do\n  echo $i\ndone"],
    tips: [
      "The {1..5} syntax is a shorthand for the sequence 1 2 3 4 5",
      "The loop body is between do and done"
    ]
  },
  {
    title: "Text Substitution with sed",
    description: "The 'sed' (stream editor) command is used for filtering and transforming text. One of its most common uses is substituting text patterns.",
    task: "Use sed to replace all occurrences of 'apple' with 'orange' in the file 'fruits.txt'.",
    hint: "The basic syntax for substitution is: sed 's/old/new/g' filename",
    example: "sed 's/hello/hi/g' greeting.txt",
    answer: ["sed 's/apple/orange/g' fruits.txt", "sed -e 's/apple/orange/g' fruits.txt"],
    tips: [
      "The 's' command stands for substitution",
      "The 'g' flag makes the replacement global (all occurrences in each line)",
      "Without the 'g' flag, only the first occurrence on each line would be replaced"
    ],
    commandRef: {
      command: "sed",
      description: "Stream editor for filtering and transforming text line by line",
      syntax: "sed [OPTIONS] 'COMMAND' [FILE...]",
      options: [
        { flag: "s/old/new/", description: "Substitute: replace 'old' with 'new'" },
        { flag: "g", description: "Global flag: replace ALL matches, not just first" },
        { flag: "-e", description: "Add a script/command to be executed" },
        { flag: "-i", description: "Edit files in-place (modify original file)" }
      ],
      examples: [
        { code: "sed 's/cat/dog/' file.txt", description: "Replace first 'cat' per line" },
        { code: "sed 's/cat/dog/g' file.txt", description: "Replace ALL 'cat' occurrences" },
        { code: "sed 's/[0-9]/#/g' file.txt", description: "Replace all digits with #" }
      ]
    }
  },
  {
    title: "In-place Editing with sed",
    description: "By default, sed sends its output to standard output. To modify files directly, use the -i option.",
    task: "Use sed to replace all occurrences of 'old' with 'new' in the file 'config.txt', modifying the file directly.",
    hint: "You need to use the -i option for in-place editing.",
    example: "sed -i 's/foo/bar/g' example.txt",
    answer: ["sed -i 's/old/new/g' config.txt"],
    tips: [
      "Be careful with in-place editing! It modifies the file without creating a backup",
      "On macOS, you may need to use -i '' to avoid an error"
    ],
    commandRef: {
      command: "sed",
      description: "Stream editor for filtering and transforming text line by line",
      syntax: "sed [OPTIONS] 'COMMAND' [FILE...]",
      options: [
        { flag: "-i", description: "Edit file in-place (modifies the original file)" },
        { flag: "-i.bak", description: "Edit in-place and create backup with .bak extension" },
        { flag: "s/old/new/g", description: "Global substitution pattern" }
      ],
      examples: [
        { code: "sed -i 's/foo/bar/g' file.txt", description: "Replace in-place (Linux)" },
        { code: "sed -i '' 's/foo/bar/g' file.txt", description: "Replace in-place (macOS)" },
        { code: "sed -i.bak 's/a/b/g' file.txt", description: "Edit and keep backup" }
      ]
    }
  },
  {
    title: "Print Lines with sed",
    description: "sed can be used to print specific lines from a file, similar to grep but with different pattern-matching capabilities.",
    task: "Use sed to print only lines 5 through 10 from the file 'data.log'.",
    hint: "You can specify a range of lines to print using sed.",
    example: "sed -n '1,3p' file.txt (prints lines 1-3)",
    answer: ["sed -n '5,10p' data.log"],
    tips: [
      "The -n option suppresses automatic printing of lines",
      "The 'p' command tells sed to print the specified lines",
      "You can combine this with other sed commands for complex filtering"
    ],
    commandRef: {
      command: "sed",
      description: "Stream editor for filtering and transforming text line by line",
      syntax: "sed [OPTIONS] 'COMMAND' [FILE...]",
      options: [
        { flag: "-n", description: "Suppress automatic printing (quiet mode)" },
        { flag: "p", description: "Print command - output the matched lines" },
        { flag: "5,10", description: "Line range - from line 5 to line 10" }
      ],
      examples: [
        { code: "sed -n '5p' file.txt", description: "Print only line 5" },
        { code: "sed -n '1,10p' file.txt", description: "Print lines 1 through 10" },
        { code: "sed -n '/error/p' log.txt", description: "Print lines containing 'error'" }
      ]
    }
  },
  {
    title: "Basic awk Usage",
    description: "awk is a powerful text processing tool that works on data as rows and columns.",
    task: "Use awk to print the first column of each line in 'data.csv'.",
    hint: "awk uses $1, $2, etc. to refer to columns in each line.",
    example: "awk '{print $2}' data.txt (prints the second column)",
    answer: ["awk '{print $1}' data.csv", "awk '{print $1}' data.csv"],
    tips: [
      "By default, awk splits lines on whitespace",
      "For CSV files, you might want to specify the delimiter using -F",
      "The entire command must be enclosed in quotes"
    ],
    commandRef: {
      command: "awk",
      description: "Pattern scanning and text processing language. Processes text as rows (lines) and columns (fields)",
      syntax: "awk [OPTIONS] 'PATTERN {ACTION}' [FILE...]",
      options: [
        { flag: "$1, $2...", description: "Field variables - $1 is first column, $2 is second, etc." },
        { flag: "$0", description: "The entire current line" },
        { flag: "-F", description: "Set the field separator (default is whitespace)" },
        { flag: "NF", description: "Number of fields in current line" },
        { flag: "NR", description: "Current line number" }
      ],
      examples: [
        { code: "awk '{print $1}' file.txt", description: "Print first column of each line" },
        { code: "awk '{print $1, $3}' file.txt", description: "Print columns 1 and 3" },
        { code: "awk '{print NR, $0}' file.txt", description: "Print line numbers with content" }
      ]
    }
  },
  {
    title: "Field Separator in awk",
    description: "awk allows you to specify a custom field separator using the -F option.",
    task: "Use awk to print the third column of a CSV file 'employees.csv', where fields are separated by commas.",
    hint: "You need to specify the comma as the field separator.",
    example: "awk -F':' '{print $1}' /etc/passwd",
    answer: ["awk -F, '{print $3}' employees.csv", "awk -F ',' '{print $3}' employees.csv"],
    tips: [
      "The -F option can be followed by the delimiter with or without a space",
      "You can use any character or string as a delimiter",
      "For special characters, you might need to escape them"
    ],
    commandRef: {
      command: "awk",
      description: "Pattern scanning and text processing language with custom field separators",
      syntax: "awk -F'DELIMITER' '{ACTION}' [FILE...]",
      options: [
        { flag: "-F,", description: "Use comma as field separator (CSV)" },
        { flag: "-F:", description: "Use colon as field separator" },
        { flag: "-F'\\t'", description: "Use tab as field separator (TSV)" }
      ],
      examples: [
        { code: "awk -F, '{print $2}' data.csv", description: "Print 2nd column of CSV" },
        { code: "awk -F: '{print $1}' /etc/passwd", description: "Print usernames from passwd" },
        { code: "awk -F'|' '{print $1,$3}' file.txt", description: "Pipe-delimited fields" }
      ]
    }
  },
  {
    title: "Filtering with awk",
    description: "awk can filter lines based on conditions before processing them.",
    task: "Use awk to print only lines where the second column is greater than 50 in the file 'scores.txt'.",
    hint: "You can add a condition before the action in awk.",
    example: "awk '$3 == \"USA\"' data.txt (prints lines where 3rd column is USA)",
    answer: ["awk '$2 > 50 {print}' scores.txt", "awk '$2 > 50' scores.txt"],
    tips: [
      "The default action is to print the entire line, so {print} is optional",
      "You can use various comparison operators: ==, !=, <, >, <=, >=",
      "For string comparisons, use quotes: $1 == \"text\""
    ],
    commandRef: {
      command: "awk",
      description: "Pattern scanning with conditional filtering",
      syntax: "awk 'CONDITION {ACTION}' [FILE...]",
      options: [
        { flag: "==", description: "Equal to (numbers or strings)" },
        { flag: "!=", description: "Not equal to" },
        { flag: "> < >= <=", description: "Numeric comparisons" },
        { flag: "~", description: "Matches regex pattern" },
        { flag: "&&  ||", description: "AND / OR for combining conditions" }
      ],
      examples: [
        { code: "awk '$2 > 100' data.txt", description: "Lines where column 2 > 100" },
        { code: "awk '$1 == \"admin\"' users.txt", description: "Lines where column 1 is 'admin'" },
        { code: "awk '$3 > 50 && $3 < 100' file.txt", description: "Column 3 between 50-100" }
      ]
    }
  }
];

// Expand the base lessons to ~100 items by repeating them
const bashLessons = [];
for (let i = 0; i < 100; i++) {
  bashLessons.push({ ...baseBashLessons[i % baseBashLessons.length] });
}

export default bashLessons;
